import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverAnchor,
  Portal,
  Text,
  Tooltip,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  faArrowsDownToLine,
  faArrowsLeftRight,
  faCheck,
  faChevronDown,
  faClone,
  faColumns,
  faCompress,
  faCopy,
  faEllipsisH,
  faExpand,
  faEyeSlash,
  faFileExport,
  faLink,
  faLinkSlash,
  faPen,
  faSitemap,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { Row } from '@tanstack/react-table';
import { isEmpty, isEqual, some } from 'lodash';
import { ReactElement, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation } from 'urql';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as DynamicLinkingIcon } from '../../../assets/icons/dynamic-linking.svg';
import { ReactComponent as IsolationIcon } from '../../../assets/icons/isolation.svg';

import {
  ApplyPromptLinkingDocument,
  CreateOrderEntryInput,
  ElementIdentifierDbidTypeInput,
  ForgeAttributeQueryTypeInput,
  ForgeAttributeType,
  ForgeAttributeTypeInput,
  GetOrderEntryTemplatesPageDocument,
  OrderColumnDeepFragment,
  OrderColumnInput,
  OrderEntryTemplateDeepFragment,
  PropertySetType,
  UnitSettingsType,
} from '../../../gql/graphql';
import { usePropertyContext } from '../../../hooks/property-resolving';
import { hasDbIds } from '../../../services/viewer-services';
import { useSelection } from '../../../services/viewer/selection';
import ColumnManagementModal from '../../common/ColumnManagementModal';
import ConfirmDeletionModal from '../../common/ConfirmDeletionModal';
import { DbIdsPerModel, useViewer } from '../../common/ForgeViewer';
import { usePanelFullscreen } from '../../common/PanelFullscreenContext';
import SparkelIcon from '../../common/icon/SparkelIcon';
import ConvertAndLinkModal from './ConvertAndLinkModal';
import ConvertAndUnlinkModal from './ConvertAndUnlinkModal';
import { DynamicDbidsPopover } from './DynamicDbidsPopover';
import FlattenModal from './FlattenModal';
import { GroupByAttributePopover } from './GroupByAttributePopover';
import { TableEntry } from './OrderEntriesPanel';
import ResourceTemplateModal from './ResourceTemplateModal';
import ConvertAndUnlinkAllModal from './UnlinkAllModal';
import MoveOrCopyItemsModal from './MoveOrCopyItemsModal';
import { useUserTenant } from 'src/services/auth-info';
import { Point } from 'src/domain/geometry/geometric-types';
import { useActiveModels } from 'src/hooks/active-models';

const pluralize = (single: string, multiple: string, n: number) =>
  n === 1 ? single : multiple;

type OrderEntriesPanelToolbarProps = {
  projectId: string;
  selectedRootRows: Row<TableEntry>[];
  selectedRowDbIds: DbIdsPerModel;
  propertySets: PropertySetType[];
  isSubmittingEditForm: boolean;
  isEditingOrAddingRow: boolean;
  isUpdatingTable: boolean;
  isHistoryMode: boolean;
  orderColumns: OrderColumnDeepFragment[];
  isIsolationToggleActive: boolean;
  rowColors: string[];
  setIsolationToggleActive: (isIsolating: boolean) => void;
  createOrderEntry: (
    orderEntryInput: CreateOrderEntryInput
  ) => Promise<string | void>;
  handleCreateFromTemplateClick: (
    templateId: string,
    title: string,
    color: string,
    query: ForgeAttributeQueryTypeInput | null,
    dbids: ElementIdentifierDbidTypeInput[] | null
  ) => Promise<void>;
  handleEditRowClick: () => void;
  handleCancelEdit: () => void;
  handleDeleteEntries: () => Promise<void>;
  handleGroupBySubmit: (attribute: ForgeAttributeTypeInput[]) => Promise<void>;
  handleRemoveGroupBy: () => Promise<void>;
  handleDynamicLinkingSubmit: (
    query: ForgeAttributeQueryTypeInput
  ) => Promise<void>;
  handleRemoveDynamicLinking: () => Promise<void>;
  handleClearPromptLinking: () => Promise<void>;
  handleFlattenSubmit: () => Promise<void>;
  handleColumnManagementClick: (columns: OrderColumnInput[]) => Promise<void>;
  handleExportToXlsx: () => Promise<void>;
  handleAddItemClick: () => Promise<void>;
  handleCreateTemplateClick: (templateName: string) => Promise<void>;
  handleMoveOrCopyItemsClick: (orderId: string) => Promise<void>;
  handleDuplicateClick: () => Promise<void>;
  handleResetColumnWidths: () => void;
  linkElementsToRow: () => Promise<void>;
  unlinkElementsFromRow: () => Promise<void>;
  unlinkAllElementsFromRow: () => Promise<void>;
  hideElementsForRows: () => void;
  deleteCustomColumn: (columnId: string) => Promise<void>;
  readOnly: boolean;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
};

// eslint-disable-next-line complexity
export function OrderEntriesPanelToolbar({
  projectId,
  selectedRootRows,
  selectedRowDbIds,
  propertySets,
  isSubmittingEditForm,
  isEditingOrAddingRow,
  orderColumns,
  isIsolationToggleActive,
  isHistoryMode,
  isUpdatingTable,
  setIsolationToggleActive,
  handleCreateFromTemplateClick,
  handleEditRowClick,
  handleCancelEdit,
  handleDeleteEntries,
  handleGroupBySubmit,
  handleRemoveGroupBy,
  handleDynamicLinkingSubmit,
  handleRemoveDynamicLinking,
  handleClearPromptLinking,
  handleFlattenSubmit,
  handleExportToXlsx,
  handleAddItemClick,
  handleMoveOrCopyItemsClick,
  handleDuplicateClick,
  handleCreateTemplateClick,
  handleResetColumnWidths,
  handleColumnManagementClick,
  linkElementsToRow,
  unlinkElementsFromRow,
  unlinkAllElementsFromRow,
  hideElementsForRows,
  deleteCustomColumn,
  readOnly,
  projectSettings,
}: OrderEntriesPanelToolbarProps): ReactElement {
  const { colorMode } = useColorMode();
  const { propertyContext } = usePropertyContext(projectId);
  const { isFullscreen, toggleFullscreen } = usePanelFullscreen();

  const { viewer, isAllModelsLoaded } = useViewer();
  const { activeModels } = useActiveModels(projectId);

  const [, applyPromptLinking] = useMutation(ApplyPromptLinkingDocument);

  const handleApplyPromptLinking = useCallback(
    async ({
      prompt,
      usePromptForLinking,
    }: {
      prompt: string;
      usePromptForLinking: boolean;
    }) => {
      const orderEntryIds = selectedRootRows
        .map((row) => row.original.id)
        .filter((id): id is string => typeof id === 'string');
      if (orderEntryIds.length === 0) return;
      const activeComplete = (activeModels ?? [])
        .filter((m) => m.status === 'COMPLETE')
        .map((m) => ({
          id: m.id,
          urn: m.forgeUrn ?? '',
          version: m.version,
          name: m.name,
        }));
      await applyPromptLinking({
        input: {
          orderEntryIds,
          prompt,
          usePromptForLinking,
          projectId,
          activeModels: activeComplete,
        },
      });
    },
    [applyPromptLinking, selectedRootRows, projectId, activeModels]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const { selectedDbIds } = useSelection();

  const { isProtan, isFreeContractor } = useUserTenant();

  const { t } = useTranslation('table');

  const [{ data: resourceTemplates }] = useQuery({
    query: GetOrderEntryTemplatesPageDocument,
  });

  const {
    isOpen: showDeleteOrderEntriesModal,
    onOpen: onDeleteOrderEntriesModalOpen,
    onClose: onDeleteOrderEntriesModalClose,
  } = useDisclosure();
  const {
    isOpen: showFlattenModal,
    onOpen: onFlattenModalOpen,
    onClose: onFlattenModalClose,
  } = useDisclosure();

  const {
    isOpen: showLinkModal,
    onOpen: onLinkModalOpen,
    onClose: onLinkModalClose,
  } = useDisclosure();

  const {
    isOpen: showUnlinkModal,
    onOpen: onUnlinkModalOpen,
    onClose: onUnlinkModalClose,
  } = useDisclosure();

  const {
    isOpen: showUnlinkAllModal,
    onOpen: onUnlinkAllModalOpen,
    onClose: onUnlinkAllModalClose,
  } = useDisclosure();

  const {
    isOpen: showResourceTemplateModal,
    onOpen: onResourceTemplateModalOpen,
    onClose: onResourceTemplateModalClose,
  } = useDisclosure();

  const {
    isOpen: showMoveOrCopyItemsModal,
    onOpen: onMoveOrCopyItemsModalOpen,
    onClose: onMoveOrCopyItemsModalClose,
  } = useDisclosure();

  const {
    isOpen: showColumnManagementModal,
    onOpen: onColumnManagementModalOpen,
    onClose: onColumnManagementModalClose,
  } = useDisclosure();

  const viewerButtonsDisabled = !viewer || !hasDbIds(selectedRowDbIds);

  const noElementsSelected = !hasDbIds(selectedDbIds);

  const linkSelectedButtonsDisabled =
    !isAllModelsLoaded || noElementsSelected || selectedRootRows.length === 0;

  const isSomeSelectedRowDynamic = some(
    selectedRootRows,
    (row) => !!row.original.elementIdentifiersQuery
  );

  const [isLinking, setLinking] = useState(false);

  const linkElementsToRowInternal = async () => {
    setLinking(true);
    try {
      await linkElementsToRow();
    } finally {
      setLinking(false);
    }
  };

  const [isUsingCascader, setIsUsingCascader] = useState(false);
  const [isGroupingPopoverOpen, setIsGroupingPopoverOpen] = useState(false);
  const [isDynamicLinkingPopoverOpen, setIsDynamicLinkingPopoverOpen] =
    useState(false);

  useHotkeys('shift+e', handleEditRowClick, {
    enabled: !isEditingOrAddingRow,
  });

  let groupByAttributes: ForgeAttributeType[] | null = null;
  if (
    selectedRootRows.length > 0 &&
    selectedRootRows.every((row) =>
      isEqual(
        row.original.groupByAttributes,
        selectedRootRows[0].original.groupByAttributes
      )
    )
  ) {
    groupByAttributes = selectedRootRows[0].original.groupByAttributes ?? null;
  }

  let elementIdentifiersQuery = null;
  let defaultUsePromptForLinking = false;
  let defaultPromptForLinking: string | undefined = undefined;
  if (
    selectedRootRows.length > 0 &&
    selectedRootRows.every((row) =>
      isEqual(
        row.original.elementIdentifiersQuery,
        selectedRootRows[0].original.elementIdentifiersQuery
      )
    )
  ) {
    elementIdentifiersQuery =
      selectedRootRows[0].original.elementIdentifiersQuery;
  }

  if (
    selectedRootRows.length > 0 &&
    selectedRootRows.every(
      (row) =>
        row.original.usePromptForLinking ===
        selectedRootRows[0].original.usePromptForLinking
    )
  ) {
    defaultUsePromptForLinking =
      !!selectedRootRows[0].original.usePromptForLinking;
  }
  if (
    selectedRootRows.length > 0 &&
    selectedRootRows.every(
      (row) =>
        row.original.promptForLinking ===
        selectedRootRows[0].original.promptForLinking
    )
  ) {
    defaultPromptForLinking =
      selectedRootRows[0].original.promptForLinking ?? undefined;
  }

  const renderResourceTemplate = (template: OrderEntryTemplateDeepFragment) => (
    <MenuItem
      key={template.id}
      onClick={() =>
        handleCreateFromTemplateClick(
          template.id,
          template.templateName,
          '',
          template.elementIdentifiersQuery || null,
          template.elementIdentifiersDbid || null
        )
      }
    >
      <Flex width={'100%'}>
        <Text flex={1} noOfLines={1} wordBreak={'break-all'}>
          {template.templateName}
        </Text>
        <HStack marginLeft={2}>
          {template.elementIdentifiersQuery ? (
            <Tooltip
              label={template.elementIdentifiersQuery.predicates.map(
                (predicate, idx) => (
                  <Text key={idx}>
                    {`${predicate.attribute.category} / ${predicate.attribute.name}`}
                  </Text>
                )
              )}
            >
              {/* Tooltip didn't position correctly directly on the SparkelIcon, hence the Box */}
              <Box>
                <SparkelIcon color={'teal.300'} as={DynamicLinkingIcon} />
              </Box>
            </Tooltip>
          ) : undefined}
          {template.groupByAttributes ? (
            <Tooltip
              label={template.groupByAttributes.map((group, idx) => (
                <Text key={idx}>{`${group.category} / ${group.name}`}</Text>
              ))}
            >
              <SparkelIcon color={'blue.300'} icon={faSitemap} />
            </Tooltip>
          ) : undefined}
        </HStack>
      </Flex>
    </MenuItem>
  );

  const groupingPopoverRef = useRef<HTMLDivElement | null>(null);
  const dynamicLinkingPopoverRef = useRef<HTMLDivElement | null>(null);

  // Since the cascader is rendered in a portal, outside clicks needs to be handled explicitly
  useOutsideClick({
    ref: groupingPopoverRef,
    handler: () => {
      if (!isUsingCascader) {
        // Only close popover on outside click if no cascader is in use
        setIsGroupingPopoverOpen(false);
      }
    },
  });

  useOutsideClick({
    ref: dynamicLinkingPopoverRef,
    handler: () => {
      if (!isUsingCascader) {
        // Only close popover on outside click if no cascader is in use
        setIsDynamicLinkingPopoverOpen(false);
      }
    },
  });

  const activeIconColor = useColorModeValue('teal.500', 'teal.200');
  const activePromptIconColor = useColorModeValue('purple.500', 'purple.200');
  const activePromptIconBackgroundColor = useColorModeValue(
    'purple.50',
    'purple.900'
  );

  const isGroupedRow =
    selectedRootRows.length > 0 &&
    selectedRootRows.every((row) => !isEmpty(row.original.groupByAttributes));

  const isDynamicallyLinked =
    selectedRootRows.length > 0 &&
    selectedRootRows.every((row) => !!row.original.elementIdentifiersQuery);

  const isPromptLinked =
    selectedRootRows.length > 0 &&
    selectedRootRows.every((row) => !!row.original.usePromptForLinking);

  const iconButtonBackgroundColor = useColorModeValue('teal.50', undefined);

  return (
    <>
      <Flex columnGap={2} overflow="auto">
        {!isEditingOrAddingRow ? (
          <>
            <Flex
              backgroundColor={colorMode === 'light' ? 'gray.50' : 'gray.900'}
              columnGap={1}
              px={2}
              borderRadius={16}
              display={selectedRootRows.length > 0 ? undefined : 'none'}
            >
              {selectedRootRows.length > 0 ? (
                <>
                  <Center>
                    <Box
                      color={colorMode === 'light' ? 'gray.400' : 'gray.500'}
                      ml={2}
                      mr={0}
                      display={'inline-block'}
                      fontSize={'xs'}
                      verticalAlign={'bottom'}
                    >
                      {pluralize(
                        t('selected-items-single', {
                          num: selectedRootRows.length,
                        }),
                        t('selected-items-multiple', {
                          num: selectedRootRows.length,
                        }),
                        selectedRootRows.length
                      )}
                    </Box>
                  </Center>
                  <Center>
                    <Divider orientation="vertical" height="70%" />
                  </Center>
                </>
              ) : null}

              {!isProtan && (
                <>
                  <Box ref={groupingPopoverRef}>
                    <Popover
                      closeOnBlur={false}
                      isOpen={isGroupingPopoverOpen}
                      isLazy
                      flip={false}
                      modifiers={[
                        {
                          name: 'preventOverflow',
                          options: {
                            altAxis: true,
                          },
                        },
                      ]}
                    >
                      <PopoverAnchor>
                        <Box>
                          <Tooltip label={t('breakdown.tooltip')}>
                            <IconButton
                              aria-label="breakdown-item"
                              icon={
                                <SparkelIcon
                                  icon={faSitemap}
                                  color={
                                    isGroupedRow ? activeIconColor : undefined
                                  }
                                />
                              }
                              isActive={isGroupedRow}
                              backgroundColor={
                                isGroupedRow
                                  ? iconButtonBackgroundColor
                                  : undefined
                              }
                              size={'xs'}
                              colorScheme="teal"
                              variant={'toggle'}
                              display={readOnly ? 'none' : undefined}
                              isDisabled={selectedRootRows.length === 0}
                              onClick={() =>
                                setIsGroupingPopoverOpen(!isGroupingPopoverOpen)
                              }
                            />
                          </Tooltip>
                        </Box>
                      </PopoverAnchor>

                      <GroupByAttributePopover
                        isOpen={isGroupingPopoverOpen}
                        isUpdatingTable={isUpdatingTable}
                        onUsingCascader={setIsUsingCascader}
                        propertySets={propertySets}
                        defaultAttributes={groupByAttributes}
                        onGroupBy={handleGroupBySubmit}
                        onUngroup={handleRemoveGroupBy}
                      />
                    </Popover>
                  </Box>
                  <Center display={readOnly ? 'none' : undefined}>
                    <Divider orientation="vertical" height="70%" />
                  </Center>

                  <Popover
                    closeOnBlur={false}
                    isOpen={isDynamicLinkingPopoverOpen}
                    isLazy
                    flip={false}
                    modifiers={[
                      {
                        name: 'preventOverflow',
                        options: {
                          altAxis: true,
                        },
                      },
                    ]}
                  >
                    <PopoverAnchor>
                      <Box>
                        <Tooltip label={t('dynamic-link.tooltip')}>
                          <IconButton
                            aria-label="Dynamic Link"
                            icon={
                              <SparkelIcon
                                as={DynamicLinkingIcon}
                                color={
                                  isDynamicallyLinked
                                    ? isPromptLinked
                                      ? activePromptIconColor
                                      : activeIconColor
                                    : undefined
                                }
                              />
                            }
                            isActive={isDynamicallyLinked}
                            backgroundColor={
                              isDynamicallyLinked
                                ? iconButtonBackgroundColor
                                : undefined
                            }
                            _active={{
                              backgroundColor: isPromptLinked
                                ? activePromptIconBackgroundColor
                                : iconButtonBackgroundColor,
                            }}
                            size={'xs'}
                            colorScheme="teal"
                            variant={'toggle'}
                            display={readOnly ? 'none' : undefined}
                            isDisabled={selectedRootRows.length === 0}
                            onClick={() =>
                              setIsDynamicLinkingPopoverOpen(
                                !isDynamicLinkingPopoverOpen
                              )
                            }
                          />
                        </Tooltip>
                      </Box>
                    </PopoverAnchor>
                    <Portal>
                      <Box ref={dynamicLinkingPopoverRef}>
                        <DynamicDbidsPopover
                          isOpen={isDynamicLinkingPopoverOpen}
                          isUpdatingTable={isUpdatingTable}
                          onUsingCascader={setIsUsingCascader}
                          onLink={handleDynamicLinkingSubmit}
                          onUnlink={handleRemoveDynamicLinking}
                          onClearPromptLinking={handleClearPromptLinking}
                          propertySets={propertySets}
                          defaultQuery={elementIdentifiersQuery}
                          defaultUsePromptForLinking={
                            defaultUsePromptForLinking
                          }
                          defaultPromptForLinking={defaultPromptForLinking}
                          propertyContext={propertyContext}
                          projectSettings={projectSettings}
                          onApplyPromptLinking={handleApplyPromptLinking}
                        />
                      </Box>
                    </Portal>
                  </Popover>

                  <Tooltip label={t('link.tooltip')}>
                    <Button
                      aria-label="Link elements"
                      leftIcon={<SparkelIcon icon={faLink} />}
                      colorScheme="gray"
                      variant={'ghost'}
                      size="xs"
                      onClick={
                        isSomeSelectedRowDynamic
                          ? onLinkModalOpen
                          : linkElementsToRowInternal
                      }
                      paddingEnd={3}
                      isDisabled={linkSelectedButtonsDisabled}
                      isLoading={isLinking}
                      display={readOnly ? 'none' : undefined}
                    >
                      Link
                    </Button>
                  </Tooltip>
                  {noElementsSelected ? (
                    <Tooltip label={t('clear-links.tooltip')}>
                      <IconButton
                        aria-label="Clear links"
                        icon={<SparkelIcon icon={faLinkSlash} />}
                        isRound
                        colorScheme="gray"
                        variant={'ghost'}
                        size="xs"
                        onClick={onUnlinkAllModalOpen}
                        isDisabled={
                          !isAllModelsLoaded || selectedRootRows.length === 0
                        }
                        display={readOnly ? 'none' : undefined}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip label={t('unlink.tooltip')}>
                      <IconButton
                        aria-label="Unlink elements"
                        icon={<SparkelIcon icon={faLinkSlash} />}
                        isRound
                        colorScheme="gray"
                        variant={'ghost'}
                        size="xs"
                        onClick={
                          isSomeSelectedRowDynamic
                            ? onUnlinkModalOpen
                            : unlinkElementsFromRow
                        }
                        disabled={linkSelectedButtonsDisabled}
                        display={readOnly ? 'none' : undefined}
                      />
                    </Tooltip>
                  )}
                </>
              )}
              <Tooltip label={t('delete-selected.tooltip')}>
                <IconButton
                  aria-label="Delete item(s)"
                  icon={<SparkelIcon color={'red.400'} icon={faTrash} />}
                  isRound
                  size={'xs'}
                  colorScheme="red"
                  variant={'ghost'}
                  onClick={onDeleteOrderEntriesModalOpen}
                  isDisabled={selectedRootRows.length === 0}
                  display={readOnly ? 'none' : undefined}
                />
              </Tooltip>
            </Flex>
            {!isProtan && (
              <Tooltip label={t('focus-mode.tooltip')}>
                <IconButton
                  aria-label="Focus mode"
                  isRound
                  size={'xs'}
                  colorScheme={isIsolationToggleActive ? 'teal' : 'gray'}
                  variant={isIsolationToggleActive ? 'toggle' : undefined}
                  isActive={isIsolationToggleActive}
                  backgroundColor={
                    isIsolationToggleActive
                      ? iconButtonBackgroundColor
                      : undefined
                  }
                  icon={
                    <SparkelIcon
                      as={IsolationIcon}
                      color={
                        isIsolationToggleActive ? activeIconColor : undefined
                      }
                    />
                  }
                  onClick={() =>
                    setIsolationToggleActive(!isIsolationToggleActive)
                  }
                />
              </Tooltip>
            )}
          </>
        ) : null}

        {isEditingOrAddingRow ? (
          <>
            <IconButton
              aria-label="cancel edit"
              isRound
              size="xs"
              colorScheme="gray"
              onClick={handleCancelEdit}
              icon={<SparkelIcon icon={faTimes} />}
            />
            <IconButton
              aria-label="submit row"
              isRound
              size="xs"
              colorScheme="brand"
              type="submit"
              form="edit-table-entry-form"
              isLoading={isSubmittingEditForm}
              icon={
                <SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />
              }
            />
          </>
        ) : (
          <>
            {!isProtan && !isFreeContractor && (
              <ButtonGroup isAttached>
                <Tooltip label={t('add-item.tooltip')}>
                  <Button
                    display={readOnly ? 'none' : undefined}
                    size="xs"
                    aria-label="add new item"
                    onClick={handleAddItemClick}
                  >
                    {t('add-item')}
                  </Button>
                </Tooltip>

                <Menu isLazy>
                  <Tooltip label={t('add-item-from-template')}>
                    <MenuButton
                      as={IconButton}
                      display={readOnly ? 'none' : undefined}
                      size="xs"
                      aria-label="add item from template"
                      icon={
                        <SparkelIcon
                          icon={faChevronDown}
                          color={['white', 'gray.800']}
                        />
                      }
                    />
                  </Tooltip>
                  <Portal>
                    <MenuList
                      zIndex={99}
                      maxHeight={'50vh'}
                      maxWidth={'300px'}
                      overflow={'scroll'}
                    >
                      {resourceTemplates
                        ? resourceTemplates.orderEntryTemplatesConnection?.nodes
                            .sort((a, b) =>
                              a.name.localeCompare(b.name, undefined, {
                                sensitivity: 'base',
                              })
                            )
                            .map((template) => renderResourceTemplate(template))
                        : undefined}
                    </MenuList>
                  </Portal>
                </Menu>
              </ButtonGroup>
            )}
            {!isEditingOrAddingRow && !isHistoryMode ? (
              <>
                {/* For free contractors, show Excel export button directly */}
                {isFreeContractor ? (
                  <Tooltip label={t('menu-item.export-to-excel')}>
                    <IconButton
                      aria-label="Export to Excel"
                      icon={<SparkelIcon icon={faFileExport} />}
                      size="xs"
                      colorScheme="gray"
                      variant="ghost"
                      onClick={handleExportToXlsx}
                    />
                  </Tooltip>
                ) : (
                  <Menu isLazy>
                    <Tooltip label={t('more-actions.tooltip')}>
                      <MenuButton
                        aria-label="More actions"
                        as={IconButton}
                        size={'xs'}
                        maxWidth={16}
                        colorScheme="gray"
                        variant="ghost"
                      >
                        <SparkelIcon icon={faEllipsisH} />
                      </MenuButton>
                    </Tooltip>
                    <Portal>
                      <MenuList zIndex={99}>
                        <MenuItem
                          aria-label="Edit item(s)"
                          icon={<SparkelIcon icon={faPen} fixedWidth />}
                          onClick={handleEditRowClick}
                          isDisabled={selectedRootRows.length === 0}
                          display={readOnly ? 'none' : undefined}
                        >
                          {t('menu-item.edit-items')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Duplicate item"
                          icon={<SparkelIcon icon={faCopy} fixedWidth />}
                          onClick={handleDuplicateClick}
                          isDisabled={
                            selectedRootRows.length === 0 ||
                            isEditingOrAddingRow
                          }
                          display={readOnly ? 'none' : undefined}
                        >
                          {t('menu-item.duplicate-items')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Move or copy item"
                          icon={
                            <SparkelIcon icon={faArrowsLeftRight} fixedWidth />
                          }
                          onClick={onMoveOrCopyItemsModalOpen}
                          isDisabled={
                            selectedRootRows.length === 0 ||
                            isEditingOrAddingRow
                          }
                          display={
                            readOnly || isFreeContractor ? 'none' : undefined
                          }
                        >
                          {t('menu-item.move-or-copy-items')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Delete item"
                          color={'red.400'}
                          icon={
                            <SparkelIcon
                              color={'red.400'}
                              icon={faTrash}
                              fixedWidth
                            />
                          }
                          onClick={onDeleteOrderEntriesModalOpen}
                          isDisabled={
                            selectedRootRows.length === 0 ||
                            isEditingOrAddingRow
                          }
                          display={readOnly ? 'none' : undefined}
                        >
                          {t('menu-item.delete-items')}
                        </MenuItem>
                        <Divider display={readOnly ? 'none' : undefined} />
                        <MenuItem
                          aria-label="Flatten items"
                          icon={
                            <SparkelIcon icon={faArrowsDownToLine} fixedWidth />
                          }
                          onClick={onFlattenModalOpen}
                          isDisabled={
                            selectedRootRows.length === 0 ||
                            !selectedRootRows.some(
                              (row) => !isEmpty(row.original.groupByAttributes)
                            )
                          }
                          display={
                            readOnly || isFreeContractor ? 'none' : undefined
                          }
                        >
                          {t('menu-item.flatten-items')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Hide linked elements"
                          icon={<SparkelIcon icon={faEyeSlash} />}
                          onClick={hideElementsForRows}
                          isDisabled={viewerButtonsDisabled}
                          display={isFreeContractor ? 'none' : undefined}
                        >
                          {t('menu-item.hide-linked-elements')}
                        </MenuItem>
                        <Divider display={readOnly ? 'none' : undefined} />
                        <MenuItem
                          aria-label="Create item template"
                          icon={<SparkelIcon icon={faClone} fixedWidth />}
                          isDisabled={
                            selectedRootRows.length !== 1 ||
                            isEditingOrAddingRow
                          }
                          display={
                            readOnly || isFreeContractor ? 'none' : undefined
                          }
                          onClick={onResourceTemplateModalOpen}
                        >
                          {t('menu-item.create-item-template')}
                        </MenuItem>
                        <Divider display={readOnly ? 'none' : undefined} />
                        <MenuItem
                          aria-label="Export to Excel"
                          icon={<SparkelIcon icon={faFileExport} fixedWidth />}
                          onClick={handleExportToXlsx}
                        >
                          {t('menu-item.export-to-excel')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Manage columns"
                          icon={<SparkelIcon icon={faColumns} fixedWidth />}
                          onClick={onColumnManagementModalOpen}
                          display={
                            readOnly || isFreeContractor ? 'none' : undefined
                          }
                        >
                          {t('menu-item.manage-columns')}
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                )}
              </>
            ) : null}
            <Tooltip
              label={
                isFullscreen
                  ? t('table:exit-fullscreen')
                  : t('table:enter-fullscreen')
              }
            >
              <IconButton
                aria-label={
                  isFullscreen
                    ? t('table:exit-fullscreen')
                    : t('table:enter-fullscreen')
                }
                isRound
                size="xs"
                colorScheme="gray"
                variant="ghost"
                icon={
                  <SparkelIcon icon={isFullscreen ? faCompress : faExpand} />
                }
                onClick={toggleFullscreen}
              />
            </Tooltip>
            <Tooltip label={t('table:close-panel')}>
              <IconButton
                aria-label={t('table:close-panel')}
                isRound
                size="xs"
                colorScheme="gray"
                variant="ghost"
                icon={<SparkelIcon icon={faTimes} />}
                onClick={() =>
                  navigate(`/project/${projectId}/takeoff${location.search}`)
                }
              />
            </Tooltip>
          </>
        )}
      </Flex>

      <ConfirmDeletionModal
        title={t('delete-items.confirmation-title')}
        isOpen={showDeleteOrderEntriesModal}
        onConfirm={handleDeleteEntries}
        onClose={onDeleteOrderEntriesModalClose}
      />
      <FlattenModal
        isOpen={showFlattenModal}
        onFlatten={handleFlattenSubmit}
        onClose={onFlattenModalClose}
      />
      <ConvertAndLinkModal
        isOpen={showLinkModal}
        onSubmit={linkElementsToRow}
        onClose={onLinkModalClose}
      />
      <ConvertAndUnlinkModal
        isOpen={showUnlinkModal}
        onSubmit={unlinkElementsFromRow}
        onClose={onUnlinkModalClose}
      />
      <ConvertAndUnlinkAllModal
        isOpen={showUnlinkAllModal}
        onSubmit={unlinkAllElementsFromRow}
        onClose={onUnlinkAllModalClose}
      />
      <ResourceTemplateModal
        isOpen={showResourceTemplateModal}
        onSubmit={handleCreateTemplateClick}
        onClose={onResourceTemplateModalClose}
      />
      <MoveOrCopyItemsModal
        isOpen={showMoveOrCopyItemsModal}
        onSubmit={handleMoveOrCopyItemsClick}
        onClose={onMoveOrCopyItemsModalClose}
      />
      <ColumnManagementModal
        isOpen={showColumnManagementModal}
        onSubmit={handleColumnManagementClick}
        onClose={onColumnManagementModalClose}
        deleteCustomColumn={deleteCustomColumn}
        resetColumnWidths={handleResetColumnWidths}
        orderColumns={orderColumns}
      ></ColumnManagementModal>
    </>
  );
}
/* eslint-enable complexity */
